import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Modal, Button, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { DiamondAPI } from "../../../js/api";
import { handleApiError } from "../../../js/helper/errorHandler";
import { useTable } from "../components/DataTable/useTable";
import DataTable from "../components/DataTable/DataTable";

const DiamondRates = () => {
  const [file, setFile] = useState(null);
  const { page, limit, search, setPage, setLimit, setSearch } = useTable({});
  const [pagination, setPagination] = useState(null);
  const columns = [
    { key: "index", label: "No." },
    { key: "shape", label: "Shape" },
    { key: "diamondName", label: "Diamond Name" },
    { key: "carat", label: "Carat" },
    { key: "clarity", label: "Clarity" },
    { key: "color", label: "Color" },
    { key: "size", label: "Size (MM)" },
    { key: "rate", label: "Rate (₹)" },
    { key: "actions", label: "Actions" },
  ];
  const prevColumns = [
    { key: "index", label: "No." },
    { key: "shape", label: "Shape" },
    { key: "diamondName", label: "Diamond Name" },
    { key: "carat", label: "Carat" },
    { key: "clarity", label: "Clarity" },
    { key: "color", label: "Color" },
    { key: "size", label: "Size (MM)" },
    { key: "rate", label: "Rate (₹)" },
  ];

  const emptyDiamond = {
    shape: "",
    diamondName: "",
    clarity: "",
    color: "",
    sizeMm: "",
    carat: "",
    rate: "",
  };

  const [previewRows, setPreviewRows] = useState([]);
  const [diamondList, setDiamondList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addModal, setAddModal] = useState({
    show: false,
    row: emptyDiamond,
  });
  const [editModal, setEditModal] = useState({
    show: false,
    row: null,
  });

  const fetchDiamondRates = useCallback(async (page, limit, search) => {
    try {
      setIsLoading(true);

      const res = await DiamondAPI.getRates(page, limit, search);

      if (res.data.status === 200) {
        setDiamondList(res.data.data.rates || []);
        setPagination(res.data.data.pagination || null);
      } else {
        handleApiError(res, "Fetching Diamond Rates");
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchDiamondRates(page, limit, search);
    }, 400);

    return () => clearTimeout(t);
  }, [page, limit, search, fetchDiamondRates]);

  const handleCreateDiamond = async () => {
    const { shape, diamondName, clarity, color, sizeMm, carat, rate } =
      addModal.row;
    // Basic client-side validation
    if (
      !shape ||
      !diamondName ||
      !clarity ||
      !color ||
      !sizeMm ||
      !carat ||
      !rate
    ) {
      return toast.error("All fields are required.");
    }

    try {
      setIsLoading(true);

      const res = await DiamondAPI.addRate({
        shape,
        diamondName,
        clarity,
        color,
        currency: "INR",
        sizeMm: Number(sizeMm),
        carat: Number(carat),
        rate: Number(rate),
      });

      if (res.data.status === 200) {
        toast.success("Diamond rate added!");

        setAddModal({
          show: false,
          row: emptyDiamond,
        });

        fetchDiamondRates();
      } else {
        handleApiError(res, "Creating Diamond Rate");
      }
    } catch (err) {
      handleApiError(err, "Creating Diamond Rate");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;

    setFile(f);

    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const result = [];

      workbook.SheetNames.forEach((sheetName) => {
        const shape = sheetName.trim().toUpperCase();
        const sheet = workbook.Sheets[sheetName];

        const rows = XLSX.utils.sheet_to_json(sheet, {
          defval: null,
          raw: false,
        });

        rows.forEach((row) => {
          const sizeMm =
            row["MM Size"] || row["MM SIZE"] || row["Size"] || row["SIZE"];

          const carat =
            row["Carat Weight"] ||
            row["CARAT"] ||
            row["Emerald Carat Weight"] ||
            row["Carat"];

          if (!sizeMm || !carat) return;

          Object.keys(row).forEach((key) => {
            if (
              key.toLowerCase().includes("mm") ||
              key.toLowerCase().includes("carat")
            ) {
              return;
            }

            const match = key.match(/^(.+?)\s*\((INR|USD)\)$/i);
            if (!match) return;

            const diamondName = match[1].trim();
            const currency = match[2].toUpperCase();

            const [clarity, color] = diamondName.split(" ");
            const rate = parseFloat(String(row[key]).replace(/[^0-9.]/g, ""));

            if (isNaN(rate)) return;

            result.push({
              shape,
              diamondName,
              clarity,
              color,
              currency,
              size: String(sizeMm).trim(),
              carat: parseFloat(carat),
              rate,
            });
          });
        });
      });

      setPreviewRows(result.slice(0, 5)); // show top 5 only
    };

    reader.readAsBinaryString(f);
  };

  const uploadSheet = async () => {
    if (!file) return toast.error("Please select a file first");

    Swal.fire({
      title: "Upload Diamond File?",
      text: "This will update diamond rates",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Upload",
    }).then(async (res) => {
      if (!res.isConfirmed) return;

      try {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("file", file);

        const response = await DiamondAPI.upload(formData);

        if (response.data.status === 200) {
          const summary = response.data.data?.summary || null;

          // Show summary popup
          Swal.fire({
            title: "Upload Completed",
            html: `
            <div style="text-align:left;">
              <p><strong>Total Rows:</strong> ${summary?.total_rows || 0}</p>
              <p style="color:green;"><strong>Added:</strong> ${
                summary?.added || 0
              }</p>
              <p style="color:#0d6efd;"><strong>Updated:</strong> ${
                summary?.updated || 0
              }</p>
              <p style="color:red;"><strong>Skipped:</strong> ${
                summary?.skipped || 0
              }</p>
            </div>
          `,
            icon: "success",
            confirmButtonText: "OK",
          });

          // Clear preview
          setFile(null);
          setPreviewRows([]);

          await fetchDiamondRates(page, limit, search);
        } else {
          handleApiError(response, "Uploading Diamond Sheet");
        }
      } catch (err) {
        handleApiError(err, "Uploading Diamond Sheet");
      } finally {
        setIsLoading(false);
      }
    });
  };

  const deleteDiamond = async (id) => {
    Swal.fire({
      title: "Delete Diamond?",
      text: "This will remove the diamond from the system permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (res) => {
      if (!res.isConfirmed) return;

      try {
        setIsLoading(true);
        const response = await DiamondAPI.deleteRate(id);

        if (response.data.status === 200) {
          toast.success("Diamond deleted successfully!");
          setEditModal({ show: false, row: null });
          await fetchDiamondRates(page, limit, search);
        } else {
          handleApiError(response, "Deleting Diamond Sheet");
        }
      } catch (err) {
        handleApiError(err, "Deleting Diamond Sheet");
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleUpdateRate = async () => {
    try {
      setIsLoading(true);

      const { shape, diamondName, clarity, color, sizeMm, carat, rate } =
        editModal.row;

      const res = await DiamondAPI.updateRate(editModal.row._id, {
        shape,
        diamondName,
        clarity,
        color,
        sizeMm: sizeMm,
        carat: Number(carat),
        rate: Number(rate),
      });

      if (res.data.status === 200) {
        toast.success("Diamond updated successfully!");
        setEditModal({ show: false, row: null });
        await fetchDiamondRates(page, limit, search);
      } else {
        handleApiError(res, "Updating Diamond Rate");
      }
    } catch (err) {
      handleApiError(err, "Updating Diamond Rate");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header>
              <h4 className="card-title">Diamond Rates</h4>
            </Card.Header>

            <Card.Body className="px-4" style={{ position: "relative" }}>
              {/* {isLoading && (
                <Spinner
                  animation="border"
                  variant="primary"
                  style={{ position: "absolute", top: "50%", left: "50%" }}
                />
              )} */}

              {/* FILE UPLOAD */}
              <Row className="align-items-center">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Upload Diamond Sheet (xlsx or csv)</Form.Label>

                    <div className="d-flex gap-2">
                      <Form.Control
                        type="file"
                        accept=".xlsx, .csv"
                        onChange={handleFileSelect}
                      />

                      <Button
                        variant="outline-secondary"
                        href="/files/diamond_rate_sample.xlsx"
                        target="_blank"
                        className="text-nowrap"
                      >
                        Download Sample
                      </Button>
                    </div>

                    <small className="text-muted">
                      Download the sample file to see the correct format.
                    </small>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={() =>
                        setAddModal({
                          show: true,
                          row: {
                            shape: "",
                            diamondName: "",
                            clarity: "",
                            color: "",
                            sizeMm: "",
                            carat: "",
                            rate: "",
                          },
                        })
                      }
                    >
                      + Add Diamond Rate
                    </Button>
                  </div>
                </Col>
              </Row>

              {previewRows.length > 0 && (
                <>
                  <h5 className="mt-4">Preview (Top 5 rows)</h5>
                  <DataTable
                    columns={prevColumns}
                    data={previewRows}
                    loading={isLoading}
                    showSearch={false}
                    page={page}
                    showPagination={false}
                    showLimit={false}
                    renderRow={(row, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{row.shape}</td>
                        <td>{row.diamondName}</td>
                        <td>{row.carat}</td>
                        <td>{row.clarity}</td>
                        <td>{row.color}</td>
                        <td>{row.size}</td>
                        <td>{row.rate}</td>
                      </tr>
                    )}
                  />
                  <Button variant="success" onClick={uploadSheet}>
                    Upload Sheet
                  </Button>
                </>
              )}

              <h5 className="mt-4 mb-4">Current Diamond Rates</h5>
              <DataTable
                columns={columns}
                data={diamondList}
                loading={isLoading}
                search={search}
                onSearch={setSearch}
                page={page}
                totalPages={pagination?.totalPages || 1}
                limit={limit}
                onLimitChange={setLimit}
                onPageChange={(p) => setPage(p)}
                renderRow={(p, i) => (
                  <tr key={p._id}>
                    <td>{((page || 1) - 1) * (limit || 10) + i + 1}</td>
                    <td>{p.shape}</td>
                    <td>{p.diamondName}</td>
                    <td>{p.carat}</td>
                    <td>{p.clarity}</td>
                    <td>{p.color}</td>
                    <td>{p.sizeMm}</td>
                    <td>{p.rate}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="info"
                        onClick={() =>
                          setEditModal({
                            show: true,
                            row: { ...p },
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        className="mx-2"
                        variant="danger"
                        onClick={() => deleteDiamond(p._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                )}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* EDIT MODAL */}
      <Modal
        show={editModal.show}
        onHide={() => setEditModal({ show: false, row: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Diamond Rate</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editModal.row && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Shape</Form.Label>
                <Form.Control
                  type="text"
                  value={editModal.row.shape || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, shape: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Diamond Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editModal.row.diamondName || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, diamondName: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Color</Form.Label>
                <Form.Control
                  type="text"
                  value={editModal.row.color || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, color: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Clarity</Form.Label>
                <Form.Control
                  type="text"
                  value={editModal.row.clarity || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, clarity: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Size (MM)</Form.Label>
                <Form.Control
                  type="text"
                  value={editModal.row.sizeMm || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, sizeMm: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Carat</Form.Label>
                <Form.Control
                  type="number"
                  value={editModal.row.carat || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, carat: e.target.value },
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Rate (₹)</Form.Label>
                <Form.Control
                  type="number"
                  value={editModal.row.rate || ""}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      row: { ...editModal.row, rate: e.target.value },
                    })
                  }
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditModal({ show: false, row: null })}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateRate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD NEW DIAMOND MODAL */}
      <Modal
        show={addModal.show}
        onHide={() =>
          setAddModal({
            show: false,
            row: emptyDiamond,
          })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Diamond Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Shape</Form.Label>
            <Form.Control
              type="text"
              value={addModal.row.shape}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, shape: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Diamond Name</Form.Label>
            <Form.Control
              type="text"
              value={addModal.row.diamondName}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, diamondName: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Color</Form.Label>
            <Form.Control
              type="text"
              value={addModal.row.color}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, color: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Clarity</Form.Label>
            <Form.Control
              type="text"
              value={addModal.row.clarity}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, clarity: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Size (MM)</Form.Label>
            <Form.Control
              type="number"
              value={addModal.row.sizeMm}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, sizeMm: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Carat</Form.Label>
            <Form.Control
              type="number"
              value={addModal.row.carat}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, carat: e.target.value },
                })
              }
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Rate (₹)</Form.Label>
            <Form.Control
              type="number"
              value={addModal.row.rate}
              onChange={(e) =>
                setAddModal({
                  ...addModal,
                  row: { ...addModal.row, rate: e.target.value },
                })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setAddModal({
                show: false,
                row: emptyDiamond,
              })
            }
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateDiamond}>
            Add Diamond
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DiamondRates;
